// @flow
import i18n from "i18next";
import k from "src/i18n/keys";

import React, { useState } from "react";
import { connect } from "react-redux";
import * as R from "ramda";

import type { State, RoomId, ConversationFile, UnifizeFile } from "src/types";
import {
  getSearchedGroupedFiles,
  getCurrentChatRoom,
  getAllFilesById,
} from "src/reducers";
import { searchConversationFile } from "src/actions/file";
import { addAttachment } from "src/actions/email";

import { IconButton, VStack, HStack } from "@chakra-ui/react";
import Icons from "src/icons";
import * as colors from "src/styles/constants/colors";
import OutsideClickHandler from "src/components/OutsideClickHandler";
import {
  InputWrapper,
  Input,
  FileListWrapper,
} from "src/components/Dock/Checklist/styles";
import Upload from "src/containers/chatroom/Upload";
import SectionedFileLists from "src/components/Dock/Checklist/File/SectionedFileLists";
import FileItem from "src/components/Dock/Checklist/File/FileItem";
import * as styles from "./styles";

const modalStages = {
  PICK_SOURCE: "PICK_SOURCE",
  SEARCH_FILES: "SEARCH_FILES",
};

type ModalStages = $Values<typeof modalStages>;

type Props = {
  roomId: RoomId,
  conversationFiles: ConversationFile[],
  searchedGroupFiles: Object,
  attachments: ConversationFile[],
  sending: boolean,
  searchConversationFile: Function,
  addAttachment: Function,
  allFilesById: Map<string, UnifizeFile>,
};

const moveCurrentRoomIdToFront = (roomIds: RoomId[], currentRoomId: RoomId) => {
  return roomIds.find(R.equals(currentRoomId))
    ? R.uniq(R.prepend(currentRoomId, roomIds))
    : roomIds;
};

function AttachmentsButton({
  roomId,
  conversationFiles,
  searchConversationFile,
  searchedGroupFiles,
  attachments,
  allFilesById,
  sending,
  addAttachment,
}: Props) {
  const [modalStage, setModalStage] = useState<ModalStages | null>(null);
  const [fileSearchText, setFileSearchText] = useState("");

  const uniqueFiles = R.uniqBy((item) => item.name, conversationFiles || []);

  const filteredFiles = R.differenceWith(
    (file, attachment) => file.name === attachment.name,
    uniqueFiles,
    attachments,
  );

  const searchedGroupedFiles = R.groupBy((searchedFile) =>
    String(searchedFile.chatroomId),
  )(filteredFiles);

  const searchedRoomIds = moveCurrentRoomIdToFront(
    R.keys(searchedGroupedFiles),
    roomId,
  );

  const sortedAllFiles = allFilesById
    // $FlowFixMe
    .toArray()
    .sort(
      (file1, file2) =>
        -1 * (Date.parse(file1.uploadTime) - Date.parse(file2.uploadTime)),
    );

  const startChoosingFile = () => {
    setModalStage(modalStages.PICK_SOURCE);
    setFileSearchText("");
  };

  const stopChoosingFile = () => {
    setModalStage(null);
    setFileSearchText("");
  };

  const handleFileSearch = (e) => {
    const { value } = e.target;
    setFileSearchText(value);
    searchConversationFile(value);
  };

  const chooseFile = (file) => {
    addAttachment(file);
    stopChoosingFile();
  };

  const openConversationFileSearchModal = () => {
    setModalStage(modalStages.SEARCH_FILES);
    searchConversationFile("");
  };

  if (modalStage === modalStages.PICK_SOURCE) {
    return (
      <OutsideClickHandler onClickOutside={stopChoosingFile}>
        <VStack {...styles.filePickerFrame}>
          <HStack
            onClick={openConversationFileSearchModal}
            {...styles.fileSource}
          >
            <Icons type="attachClip" width="24" />
            <span>{i18n.t(k.FILES_FROM_UNIFIZE_CONVERSATIO)}</span>
          </HStack>
          <Upload
            roomId={roomId}
            location="email"
            isEmailAttachment
            togglePopover={stopChoosingFile}
            render={(openFileBrowser) => (
              <HStack onClick={openFileBrowser} {...styles.fileSource}>
                <Icons type="uploadCloud" />
                <span>{i18n.t(k.ATTACH_FILES_FROM_YOUR_COMPUTE)}</span>
              </HStack>
            )}
          />
        </VStack>
      </OutsideClickHandler>
    );
  } else if (modalStage === modalStages.SEARCH_FILES) {
    return (
      <OutsideClickHandler onClickOutside={stopChoosingFile}>
        <VStack {...styles.filePickerFrame}>
          <InputWrapper>
            <Icons type="searchLense" />
            <Input
              type="text"
              value={fileSearchText}
              onChange={handleFileSearch}
              placeholder={i18n.t(k.SEARCH)}
              autoFocus
            />
          </InputWrapper>
          <FileListWrapper style={{ width: "100%" }}>
            {fileSearchText.length > 0 ? (
              <SectionedFileLists
                chooseFile={chooseFile}
                roomIds={searchedRoomIds}
                searchedGroupFiles={searchedGroupFiles}
                searchedGroupedFiles={searchedGroupedFiles}
              />
            ) : (
              sortedAllFiles.map((data) => (
                <FileItem
                  roomId={roomId}
                  handleClick={() => chooseFile(data)}
                  key={data.name}
                  originalName={data.originalName}
                  size={data.size}
                  name={data.name}
                />
              ))
            )}
          </FileListWrapper>
        </VStack>
      </OutsideClickHandler>
    );
  }

  return (
    <IconButton
      onClick={startChoosingFile}
      background="transparent"
      isDisabled={sending}
      icon={<Icons type="attachClip" fill={colors.inkLight} />}
    />
  );
}

const mapStateToProps = ({ app }: State) => {
  const currentRoomId = getCurrentChatRoom(app).id;
  return {
    roomId: currentRoomId,
    conversationFiles: app.files.conversationFiles,
    allFilesById: getAllFilesById(app),
    searchedGroupFiles: getSearchedGroupedFiles(app, currentRoomId),
    attachments: app.email.attachments,
    sending: app.email.sending,
  };
};

const mapDispatchToProps = {
  searchConversationFile,
  addAttachment,
};

export default connect(mapStateToProps, mapDispatchToProps)(AttachmentsButton);
